import { useEffect, useState } from "react";

import { getToken, removeTokenPair } from "apiCommandsExecutor/token";
import { getAccount } from "services/account/rest";
import { AccountDto } from "services/account/restModels";

export interface UseSessionResult {
    account: AccountDto | null;
    loading: boolean;
    refresh: () => Promise<AccountDto | null>;
    signOut: () => void;
}

/*
* Сессия админки: кто вошёл. Живёт на уровне модуля — спрашивают её и каркас приложения, и
* отдельные экраны.
*
* ⚠️ Кеш с подписчиками, а не просто переменная модуля: сессию не только читают, но и сбрасывают
* из другого места. Без рассылки SecurityLayout не узнаёт о выходе, остаётся смонтированным и
* показывает ошибки загрузки на каждом экране.
*
* Отдельного состояния «не авторизован» нет: account === null после загрузки и есть оно. Ручка
* /account закрыта авторизацией и ролевым гейтом, поэтому чужой или просроченный токен даёт 401
* либо 403, restGet возвращает null, и каркас уводит на форму входа.
* */
let session: AccountDto | null = null;
let isLoaded = false;
let inFlight: Promise<AccountDto | null> | null = null;

/*
* Поколение сессии растёт на каждый вход, выход и принудительное обновление; ответ, стартовавший
* до этого момента, отбрасывается. Без счётчика вход с протухшим токеном откатывался бы обратно
* на форму: refresh() вернул бы тот же промис из inFlight, а он приходит 401.
* */
let generation = 0;

const listeners = new Set<(account: AccountDto | null) => void>();

const publish = (account: AccountDto | null) => {
    session = account;
    isLoaded = true;
    listeners.forEach((listener) => listener(account));
};

const loadSession = (): Promise<AccountDto | null> => {
    if (isLoaded) return Promise.resolve(session);

    if (!inFlight) {
        const startedAt = generation;

        // Без токена ходить некуда: ручка закрыта авторизацией и ответит 401.
        inFlight = (getToken() ? getAccount() : Promise.resolve(null))
            .catch(() => null)
            .then((account) => {
                if (startedAt !== generation) return session;

                inFlight = null;
                publish(account);

                return account;
            });
    }

    return inFlight;
};

const invalidateSession = () => {
    generation += 1;
    inFlight = null;
};

export const useSession = (): UseSessionResult => {
    const [account, setAccount] = useState<AccountDto | null>(session);
    const [loading, setLoading] = useState<boolean>(!isLoaded);

    useEffect(() => {
        const listener = (next: AccountDto | null) => {
            setAccount(next);
            setLoading(false);
        };

        listeners.add(listener);

        if (isLoaded) {
            setAccount(session);
            setLoading(false);
        } else {
            setLoading(true);
            loadSession();
        }

        return () => {
            listeners.delete(listener);
        };
    }, []);

    return {
        account,
        loading,
        refresh: () => {
            invalidateSession();
            isLoaded = false;

            return loadSession();
        },
        signOut: () => {
            invalidateSession();
            removeTokenPair();
            // Состояние известно и без запроса — сессии нет; рассылка уводит каркас на вход.
            publish(null);
        },
    };
};
